/**
 * F-34 multi-select — PURE logic (no React, no DOM, no Yjs): the marquee box
 * and the selection-set algebra behind it.
 *
 * Everything is in BOARD units. The marquee is dragged in screen space but
 * converted to board coordinates the moment it starts (`screenToBoard`), so
 * panning or zooming mid-drag leaves the box over the same board content
 * instead of sliding across it — the same reason element geometry is stored in
 * board units at all (docs/04 §5).
 *
 * Hit rule (docs/02 F-34: "select multiple items the box TOUCHES"): an element
 * is selected when its resolved rect INTERSECTS the marquee, not when it is
 * fully contained. Grazing a corner is enough — that is Miro's behavior, and
 * with the board's mix of large images and small stickies a containment rule
 * would make big elements practically unselectable by marquee.
 */
import type { Anchor } from "@kola/shared";

import type { BoardRect } from "../../registries/toolbar-actions/index.js";

export interface Point {
  x: number;
  y: number;
}

/**
 * Minimum diagonal (board units) before a background drag counts as a marquee
 * rather than a click. Under it the gesture is a plain click on empty canvas
 * (which clears the selection) — a two-pixel wobble while clicking must never
 * turn into a selection box.
 */
export const MARQUEE_MIN_BOARD_PX = 3;

/** The box between two dragged corners, normalized to positive w/h. */
export function marqueeRect(from: Point, to: Point): BoardRect {
  return {
    x: Math.min(from.x, to.x),
    y: Math.min(from.y, to.y),
    w: Math.abs(to.x - from.x),
    h: Math.abs(to.y - from.y),
  };
}

/** True once the drag is big enough to mean "select a region". */
export function isMarquee(from: Point, to: Point): boolean {
  return Math.hypot(to.x - from.x, to.y - from.y) >= MARQUEE_MIN_BOARD_PX;
}

/**
 * Do two board rects overlap? Touching edges count (`<=`/`>=`): a zero-height
 * element (a horizontal pen-stroke bounding box, a collapsed row's contained
 * element) must still be reachable by a marquee drawn across it.
 */
export function rectsIntersect(a: BoardRect, b: BoardRect): boolean {
  return (
    a.x <= b.x + b.w && b.x <= a.x + a.w && a.y <= b.y + b.h && b.y <= a.y + a.h
  );
}

/**
 * The ids the marquee catches, in the input's order (callers pass elements in
 * (z, id) paint order, so the resulting selection is replica-stable too).
 */
export function idsInMarquee(
  rects: Iterable<{ id: string; rect: BoardRect }>,
  marquee: BoardRect,
): string[] {
  const out: string[] = [];
  for (const { id, rect } of rects) {
    if (rectsIntersect(rect, marquee)) out.push(id);
  }
  return out;
}

/**
 * The ids an IN-CELL marquee catches (2026-08-30) — the box the grid draws when
 * a left-drag never leaves the cell it started in (GridTable's `onCellMarquee`).
 *
 * Same touch-not-contain rule as `idsInMarquee`, one extra gate: an item is
 * only the cell's to select if it BELONGS to that cell, i.e. is anchored to it.
 * Overlap is deliberately not enough — a pen stroke belongs to the cell it
 * STARTED in and may be drawn across the line into its neighbour (F-32/F-36),
 * and a board-anchored element merely floating over the grid belongs to no
 * cell at all; neither may be dragged out of a box drawn in the wrong cell.
 */
export function idsInCellMarquee(
  elements: Iterable<{ id: string; anchor: Anchor; rect: BoardRect }>,
  cell: { rowId: string; colId: string },
  marquee: BoardRect,
): string[] {
  const out: string[] = [];
  for (const { id, anchor, rect } of elements) {
    if (anchor.kind !== "cell" || anchor.rowId !== cell.rowId || anchor.colId !== cell.colId) {
      continue;
    }
    if (rectsIntersect(rect, marquee)) out.push(id);
  }
  return out;
}

/**
 * Cmd/Ctrl/Shift+click: add the id when it is out, remove it when it is in.
 * Returns a NEW array (selection state is compared by identity upstream) and
 * preserves the existing order, appending a newly-added id at the end.
 */
export function toggleSelected(selected: readonly string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id];
}

/** Union of a marquee result with the selection it extends (Shift/Cmd+drag). */
export function unionSelected(selected: readonly string[], ids: readonly string[]): string[] {
  const out = [...selected];
  for (const id of ids) if (!out.includes(id)) out.push(id);
  return out;
}

/**
 * Drop ids that no longer exist — a peer deleted the element, or an undo took
 * it away. Returns the SAME array reference when nothing changed, so the caller
 * can set state unconditionally without causing a re-render (this runs on every
 * `elementsVersion` bump, i.e. on every remote element edit on the board).
 */
export function pruneSelected(
  selected: readonly string[],
  exists: (id: string) => boolean,
): readonly string[] {
  const kept = selected.filter(exists);
  return kept.length === selected.length ? selected : kept;
}
